
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "./providers/AuthProvider"; // Direct import from providers folder
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, useEffect, lazy } from "react";
import { logger } from '@/utils/logger';
import RequireAuth from "@/components/auth/RequireAuth";
import ErrorBoundary from "@/components/error/ErrorBoundary";
import GlobalErrorBoundary from "@/components/error/GlobalErrorBoundary";
import { LoadingPage } from "@/components/routing/LoadingPage";
import { LegacyRouteRedirect } from "@/components/routing/LegacyRouteRedirect";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";

// Eagerly load critical pages for better performance
import Index from "@/pages/Index";
import AuthPage from "@/pages/Auth";
import Landing from "@/pages/Landing";

// Lazy load all other routes
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CompanyPage = lazy(() => import("@/pages/Company"));
const CompanyDetailPage = lazy(() => import("@/pages/CompanyDetailPage"));
const Personas = lazy(() => import("@/pages/Personas"));
const Analysis = lazy(() => import("@/pages/Analysis"));
const Chat = lazy(() => import("@/pages/Chat"));
const PersonaChat = lazy(() => import("@/pages/PersonaChat"));
const Settings = lazy(() => import("@/pages/Settings"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const LoginDebug = lazy(() => import("@/pages/Debug/LoginDebug"));
const AccessDenied = lazy(() => import("@/pages/AccessDenied"));
const WorkspaceNotFound = lazy(() => import("@/pages/WorkspaceNotFound"));
const Admin = lazy(() => import("@/pages/Admin"));
const AuditLogs = lazy(() => import("@/pages/Admin/AuditLogs"));

// Development-only imports
const TestMaryContext = import.meta.env.DEV ? lazy(() => import("@/pages/TestMaryContext")) : null;

// Create a client with simplified retry configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
    }
  }
});

function AppDebugLogger() {
  useEffect(() => {
    logger.info("App component mounted", {
      module: "App",
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });
    
    // Log when component unmounts (shouldn't happen for App component)
    return () => {
      logger.warn("App component unmounted unexpectedly", {
        module: "App",
        timestamp: new Date().toISOString()
      });
    };
  }, []);
  
  return null;
}

function App() {
  // Removed isMounted state and render logging to improve performance
  // Let Suspense handle loading states instead
  return (
    <GlobalErrorBoundary>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <Router>
              <WorkspaceProvider>
                <AppDebugLogger />
                <Suspense fallback={<LoadingPage />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Index />} />
                    <Route path="/landing" element={<Landing />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/debug/login" element={<LoginDebug />} />
                    
                    {/* Company management - outside workspace */}
                    <Route path="/companies" element={
                      <RequireAuth>
                        <CompanyPage />
                      </RequireAuth>
                    } />
                    <Route path="/companies/:id" element={
                      <RequireAuth>
                        <CompanyDetailPage />
                      </RequireAuth>
                    } />
                    
                    {/* Settings - user level, outside workspace */}
                    <Route path="/settings" element={
                      <RequireAuth>
                        <Settings />
                      </RequireAuth>
                    } />
                    
                    {/* Admin routes */}
                    <Route path="/admin" element={
                      <RequireAuth>
                        <Admin />
                      </RequireAuth>
                    } />
                    <Route path="/admin/audit-logs" element={
                      <RequireAuth>
                        <AuditLogs />
                      </RequireAuth>
                    } />
                    
                    {/* Development-only routes */}
                    {import.meta.env.DEV && TestMaryContext && (
                      <Route path="/test-mary-context" element={
                        <RequireAuth>
                          <TestMaryContext />
                        </RequireAuth>
                      } />
                    )}
                    
                    {/* Error pages */}
                    <Route path="/access-denied" element={
                      <RequireAuth>
                        <AccessDenied />
                      </RequireAuth>
                    } />
                    <Route path="/workspace-not-found" element={
                      <RequireAuth>
                        <WorkspaceNotFound />
                      </RequireAuth>
                    } />
                    
                    {/* Legacy routes - redirect to workspace */}
                    <Route path="/dashboard" element={
                      <RequireAuth>
                        <LegacyRouteRedirect to="dashboard" />
                      </RequireAuth>
                    } />
                    <Route path="/chat" element={
                      <RequireAuth>
                        <LegacyRouteRedirect to="chat" />
                      </RequireAuth>
                    } />
                    <Route path="/personas" element={
                      <RequireAuth>
                        <LegacyRouteRedirect to="personas" />
                      </RequireAuth>
                    } />
                    <Route path="/persona-chat" element={
                      <RequireAuth>
                        <LegacyRouteRedirect to="personas" />
                      </RequireAuth>
                    } />
                    <Route path="/persona-chat/:personaId" element={
                      <RequireAuth>
                        <LegacyRouteRedirect to="personas" />
                      </RequireAuth>
                    } />
                    <Route path="/analysis" element={
                      <RequireAuth>
                        <LegacyRouteRedirect to="analysis" />
                      </RequireAuth>
                    } />
                    
                    {/* Workspace routes */}
                    <Route path="/w/:companySlug" element={
                      <RequireAuth>
                        <WorkspaceLayout />
                      </RequireAuth>
                    }>
                      <Route path="dashboard" element={<Dashboard />} />
                      <Route path="chat" element={<Chat />} />
                      <Route path="chat/p/:personaId" element={<PersonaChat />} />
                      <Route path="personas" element={<Personas />} />
                      <Route path="analysis" element={<Analysis />} />
                    </Route>
                    
                    {/* Fallback route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <Toaster />
              </WorkspaceProvider>
            </Router>
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </GlobalErrorBoundary>
  );
}

export default App;
